
import { useState } from 'react';

interface ItemNoteModalProps {
  onClose: () => void;
  onSave: (note: string) => void;
  itemName: string;
  currentNote?: string;
}

export default function ItemNoteModal({ onClose, onSave, itemName, currentNote = '' }: ItemNoteModalProps) {
  const [note, setNote] = useState(currentNote);
  const maxLength = 200;

  const quickNotes = [
    'No onions',
    'Extra sauce',
    'Well done',
    'No ice',
    'Gift wrap',
    'Fragile - Handle with care',
    'Customer request',
    'Special instructions',
  ];

  const handleQuickNote = (quickNote: string) => {
    if (note.length + quickNote.length + 2 <= maxLength) {
      setNote(prev => prev ? `${prev}, ${quickNote}` : quickNote);
    }
  };

  const handleSave = () => {
    onSave(note.trim());
    onClose();
  };

  const handleClear = () => {
    setNote('');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gray-500 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg">
              <i className="ri-sticky-note-line text-xl"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold">Add Item Note</h2>
              <p className="text-sm text-white/80 truncate max-w-xs">{itemName}</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-white/20 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Quick Notes */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Quick Notes</label>
            <div className="flex flex-wrap gap-2">
              {quickNotes.map((quickNote, index) => (
                <button
                  key={index}
                  onClick={() => handleQuickNote(quickNote)}
                  className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-full transition-colors cursor-pointer whitespace-nowrap"
                >
                  {quickNote}
                </button>
              ))}
            </div>
          </div>

          {/* Note Input */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-gray-700">Note</label>
              <span className={`text-xs ${note.length >= maxLength ? 'text-red-500' : 'text-gray-400'}`}>
                {note.length}/{maxLength}
              </span>
            </div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, maxLength))}
              placeholder="Enter special instructions or notes for this item..."
              className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent resize-none"
              autoFocus
            />
          </div>

          {/* Info */}
          <div className="bg-gray-50 rounded-lg p-3 mb-4">
            <div className="flex items-start gap-2">
              <i className="ri-information-line text-gray-500 mt-0.5"></i>
              <p className="text-xs text-gray-600">
                Item notes will be printed on the receipt and displayed in the basket. Use notes for special requests, preparation instructions, or customer preferences.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
          <button
            onClick={handleClear}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium transition-colors cursor-pointer"
          >
            Clear Note
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-5 py-2.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-check-line mr-1"></i>
              Save Note
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
